import CustomField from "components/common/CustomField";
import CustomLabel from "components/common/CustomLabel";
import DetailsPageTitle from "components/common/DetailsPageTitle";
import FormButtons from "components/common/FormButtons";
import MultipleSelect from "components/common/MultipleSelect";
import SingleSelect from "components/common/SingleSelect";
import Spinners from "components/common/Spinners";
import {
  spaceTypeOptions,
  spaceTypeOptionsForClosure,
  weekDays,
} from "components/constants/DropdownOptions";
import { format } from "date-fns";
import { ErrorMessage, Form, Formik } from "formik";
import { Calendar } from "primereact/calendar";
import { InputSwitch } from "primereact/inputswitch";
import React, { useEffect, useState } from "react";
import { Col, Container, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import { getBranchListByCity } from "store/actions/branchSlice";
import { getCityList } from "store/actions/citySlice";
import { getSpaceListByType } from "store/actions/workspaceSlice";
import { showAlert } from "store/reducers/alertReducer";
import {
  convertTo12HourObject,
  convertTo24HourFormat,
  formatDate,
  formatTime,
} from "components/common/Utils";
import { CircleX } from "lucide-react";
import TimePicker from "components/common/TimePicker";
import {
  defaultEndTime,
  defaultStartTime,
} from "components/constants/Constants";
import { addClosureSchema } from "components/common/Validation";
import {
  addClosure,
  getClosureDetails,
  updateClosure,
} from "store/actions/closureSlice";
import { CalendarPrimaryIcon } from "components/common/SVGIcons";

const initialValues = {
  cityId: "",
  branchId: "",
  hasClosedEntireBranch: false,
  spaceType: "",
  spaceId: [],
  type: 2,
  dateAndTimes: {
    startDate: "",
    endDate: "",
    startTime: "00:00",
    endTime: "00:30",
    allowedSeparateClosure: true,
    daysOfRecurrence: [],
  },
  description: "",
  descriptionAr: "",
};

const AddClosure = ({ mode }) => {
  const { t } = useTranslation();
  document.title =
    mode === "add"
      ? t("Add New Closure | Work Space")
      : t("Update Closure | Work Space");

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [searchParams] = useSearchParams();
  const id = searchParams.get("id");

  const { isLoading, loading, closureDetails } = useSelector(
    (state) => state.Closures
  );

  const { spaceLists } = useSelector((state) => state.Workspace);
  const { citiesList } = useSelector((state) => state.Cities);
  const { branchesByCity } = useSelector((state) => state.Branches);

  const [formValues, setFormValues] = useState(initialValues);

  const [datetime12h, setDateTime12h] = useState(null);
  const [startTime, setStartTime] = useState(defaultStartTime);
  const [endTime, setEndTime] = useState(defaultEndTime);

  const { brandInfo } = useSelector((state) => state.auth);
  const { primaryColor } = brandInfo?.theme?.themeColors  || {};

  useEffect(() => {
    const fetchCityList = () => {
      dispatch(getCityList());
    };
    fetchCityList();

    const getDetails = () => {
      const payload = {
        id,
      };
      dispatch(getClosureDetails(payload));
    };

    mode === "edit" && getDetails();
  }, []);

  useEffect(() => {
    if (mode === "edit" && closureDetails) {
      const {
        cityId,
        branchId,
        hasClosedEntireBranch,
        spaceType,
        spaceId,
        type,
        dateAndTimes,
        description,
        descriptionAr,
      } = closureDetails || {};

      const { startTime, endTime, daysOfRecurrence } = dateAndTimes || {};
      const updatedDateAndTimes =
        type === 2
          ? {
              startTime: convertTo12HourObject(startTime),
              endTime: convertTo12HourObject(endTime),
              daysOfRecurrence: daysOfRecurrence || [],
            }
          : dateAndTimes;

      if (type === 2) {
        setStartTime(convertTo12HourObject(dateAndTimes.startTime));
        setEndTime(convertTo12HourObject(dateAndTimes.endTime));
      }

      const updatedSpaceIds = spaceId?.map((item) => item?._id) || [];

      cityId && fetchBranchesByCity(cityId);

      !hasClosedEntireBranch && spaceType && fetchSpaceListByType(spaceType);

      setFormValues((prevValues) => ({
        ...prevValues,
        cityId,
        branchId,
        hasClosedEntireBranch,
        spaceType,
        spaceId: updatedSpaceIds,
        type,
        dateAndTimes: updatedDateAndTimes,
        description,
        descriptionAr,
      }));
    }
  }, [mode, closureDetails]);

  const fetchBranchesByCity = (cityId) => {
    const payload = {
      cityId,
    };
    dispatch(getBranchListByCity(payload));
  };

  const fetchSpaceListByType = (spaceTypes, branchId) => {
    console.log(branchId);

    const payload = {
      spaceTypes: [spaceTypes],
      branchId: branchId,
    };
    dispatch(getSpaceListByType(payload));
  };

  const handleOnSubmit = (values) => {
    const payload = { ...values };

    // Remove spaceType & spaceId if hasClosedEntireBranch is true
    if (values.hasClosedEntireBranch) {
      delete payload.spaceType;
      delete payload.spaceId;
    }

    // Clean dateAndTimes based on type
    if (values.type === 1) {
      // keep only: startDate, endDate, startTime, endTime

      payload.dateAndTimes = {
        startDate: values?.dateAndTimes?.startDate,
        endDate: values?.dateAndTimes?.endDate,
        startTime: values?.dateAndTimes?.startTime,
        endTime: values?.dateAndTimes?.endTime,
      };
    } else if (values.type === 2) {
      // keep only: startTime, endTime, daysOfRecurrence
      payload.dateAndTimes = {
        startTime: convertTo24HourFormat(startTime),
        endTime: convertTo24HourFormat(endTime),
        daysOfRecurrence: values?.dateAndTimes?.daysOfRecurrence,
      };
    }

    if (mode === "add") {
      dispatch(addClosure(payload))
        .then((action) => {
          const { statusCode, success, message } = action?.payload;
          if (statusCode === 200 && success) {
            dispatch(showAlert({ text: message, type: "success" }));
            navigate(-1);
          } else {
            dispatch(showAlert({ text: message, type: "error" }));
          }
        })
        .catch((err) => {});
    } else if (mode === "edit") {
      console.log(values.type);

      payload.id = id;
      dispatch(updateClosure(payload))
        .then((action) => {
          const { statusCode, success, message } = action?.payload;
          if (statusCode === 200 && success) {
            dispatch(showAlert({ text: message, type: "success" }));
            navigate(-1);
          } else {
            dispatch(showAlert({ text: message, type: "error" }));
          }
        })
        .catch((err) => {});
    }
  };

  const handleTimeSelect = (values, setFieldValue, datetime12h) => {
    if (!datetime12h) {
      return dispatch(showAlert({ text: t("Select Date"), type: "error" }));
    }

    const formattedDate = format(datetime12h, "yyyy-MM-dd'T'00:00:00.000'Z'");
    const formattedStartTime = convertTo24HourFormat(startTime);
    const formattedEndTime = convertTo24HourFormat(endTime);

    const startDate = values?.dateAndTimes?.startDate;
    const startTimeVal = values?.dateAndTimes?.startTime;

    // If startDate and startTime already exist
    if (startDate && startTimeVal) {
      const startDateTime = new Date(
        `${startDate.split("T")[0]}T${startTimeVal}`
      );
      const endDateTime = new Date(
        `${formattedDate.split("T")[0]}T${formattedEndTime}`
      );

      // Check if endDate/time is the same as startDate/time
      if (formattedDate === startDate && formattedEndTime === startTimeVal) {
        return dispatch(
          showAlert({
            text: t("End date/time cannot be the same as start date/time"),
            type: "error",
          })
        );
      }

      // Check if endDate/time is before startDate/time
      if (endDateTime < startDateTime) {
        return dispatch(
          showAlert({
            text: t("End date/time cannot be before start date/time"),
            type: "error",
          })
        );
      }

      setFieldValue("dateAndTimes.endDate", formattedDate);
      setFieldValue("dateAndTimes.endTime", formattedEndTime);
    } else {
      setFieldValue("dateAndTimes.startDate", formattedDate);
      setFieldValue("dateAndTimes.startTime", formattedStartTime);
    }
  };

  return (
    <React.Fragment>
      <div className="page-content common_page add_workshop_page add_closure">
        {loading && mode === "edit" ? (
          <Spinners />
        ) : (
          <Container fluid>
            <Row>
              <DetailsPageTitle
                title={
                  mode === "add" ? t("Add New Closure") : t("Edit Closure")
                }
              />
              <Formik
                initialValues={formValues}
                validationSchema={addClosureSchema}
                enableReinitialize={true}
                onSubmit={handleOnSubmit}
              >
                {({ values, setFieldValue, errors, touched }) => (
                  <Form>
                    <div className="common_wrapper mt-3">
                      <div className="bg_white br_14 p_23_30 border_eee">
                        <h2 className="fs_20 mb-4">{t("Location")}</h2>
                        <Row className="">
                          <Col lg={6}>
                            <div className="form_field">
                              <CustomLabel htmlFor="cityId" label={t("City")} />
                              <SingleSelect
                                options={citiesList?.map((item) => ({
                                  name: item?.name,
                                  value: item?._id,
                                }))}
                                value={values.cityId}
                                placeholder={t("Select City")}
                                handleSingleSelectChange={(e) => {
                                  fetchBranchesByCity(e.value);
                                  setFieldValue("cityId", e.value);
                                  setFieldValue("branchId", null);
                                }}
                                className={`${
                                  errors.cityId && touched.cityId
                                    ? "invalid"
                                    : ""
                                }`}
                                error={errors?.cityId}
                              />
                            </div>
                          </Col>
                          <Col lg={6}>
                            <div className="form_field">
                              <CustomLabel
                                htmlFor="branchId"
                                label={t("Branch")}
                              />
                              <SingleSelect
                                options={branchesByCity?.map((item) => ({
                                  name: item?.name,
                                  value: item?._id,
                                }))}
                                value={values.branchId}
                                placeholder={t("Select Branch")}
                                handleSingleSelectChange={(e) => {
                                  setFieldValue("branchId", e.value);
                                  setFieldValue("spaceType", "");
                                  setFieldValue("spaceId", []);
                                }}
                                className={`${
                                  errors.branchId && touched.branchId
                                    ? "invalid"
                                    : ""
                                }`}
                                error={errors?.branchId}
                              />
                            </div>
                          </Col>
                        </Row>
                      </div>
                      <div className="bg_white br_14 p_23_30 border_eee mt-4">
                        <h2 className="fs_20 mb-4">{t("Type of Space")}</h2>
                        <Row>
                          <Col lg={6}>
                            <div className="d-flex align-items-center mb-4">
                              <InputSwitch
                                checked={values.hasClosedEntireBranch}
                                onChange={(e) => {
                                  const newValue =
                                    !values.hasClosedEntireBranch;
                                  setFieldValue(
                                    "hasClosedEntireBranch",
                                    newValue
                                  );
                                  if (newValue) {
                                    setFieldValue("spaceType", "");
                                    setFieldValue("spaceId", []);
                                  }
                                }}
                              />
                              <label
                                htmlFor="hasClosedEntireBranch"
                                className="ms-2"
                              >
                                {t("Close the entire branch")}
                              </label>
                            </div>
                          </Col>
                          <Col lg={6}></Col>
                          <Col lg={6}>
                            <div className="form_field">
                              <CustomLabel
                                htmlFor="spaceType"
                                label={t("Space Type")}
                              />
                              <SingleSelect
                                options={spaceTypeOptionsForClosure}
                                value={values.spaceType}
                                placeholder={t("Space Type")}
                                handleSingleSelectChange={(e) => {
                                  setFieldValue("spaceType", e.value);
                                  setFieldValue("spaceId", []);
                                  fetchSpaceListByType(
                                    e.value,
                                    values.branchId
                                  );
                                }}
                                className={`${
                                  errors.spaceType && touched.spaceType
                                    ? "invalid"
                                    : ""
                                }`}
                                error={errors?.spaceType}
                                disabled={values.hasClosedEntireBranch}
                              />
                            </div>
                          </Col>
                          <Col lg={6}>
                            <div className="form_field">
                              <CustomLabel
                                htmlFor="spaceId"
                                label={t("Select Room")}
                              />
                              <MultipleSelect
                                value={values.spaceId}
                                handleMultipleSelectChange={(e) => {
                                  setFieldValue("spaceId", e);
                                }}
                                options={spaceLists?.map((item) => ({
                                  name: item?.name,
                                  value: item?._id,
                                }))}
                                optionLabel="spaceId"
                                placeholder={"Select Room"}
                                maxSelectedLabels={3}
                                className={`${
                                  touched.spaceId && errors.spaceId
                                    ? "invalid"
                                    : ""
                                }`}
                                disabled={values.hasClosedEntireBranch}
                              />
                              <ErrorMessage
                                name={"spaceId"}
                                component="div"
                                className="text-danger"
                              />
                            </div>
                          </Col>
                          {values?.spaceId?.map((id) => {
                            const selectedSpace = spaceLists?.find(
                              (space) => space._id === id
                            );
                            if (!selectedSpace) return null;

                            const { imageUrls, name, cityId, branchId } =
                              selectedSpace || {};

                            return (
                              <Col lg={4}>
                                <div className="single_room_inner g_between">
                                  <div className="left d-flex align-items-center gap-3">
                                    <img
                                      src={imageUrls?.[0] || ""}
                                      alt=""
                                      width={59}
                                      height={59}
                                    />
                                    <div>
                                      <h2>{name || "-"}</h2>
                                      <p>
                                        {cityId?.name}, {branchId?.name}
                                      </p>
                                    </div>
                                  </div>
                                  <div className="right">
                                    <CircleX
                                      className="text_danger c_p"
                                      onClick={() => {
                                        const updatedSpaceIds =
                                          values?.spaceId?.filter(
                                            (space) => space !== id
                                          );
                                        setFieldValue(
                                          "spaceId",
                                          updatedSpaceIds
                                        );
                                      }}
                                    />
                                  </div>
                                </div>
                              </Col>
                            );
                          })}
                        </Row>
                      </div>

                      <div className="bg_white br_14 p_23_30 border_eee mt-4">
                        <h2 className="fs_20 mb-4">{t("Closure Type")}</h2>
                        <Row>
                          <Col lg={12}>
                            <div className="mb-3">
                              <div className="d-flex gap-3">
                                <label class="custom-radio">
                                  <input
                                    type="radio"
                                    id="oneTime"
                                    name="type"
                                    value={1}
                                    checked={values.type === 1}
                                    onChange={() => setFieldValue("type", 1)}
                                  />
                                  <span class="checkmark"></span>
                                  {t("One-Time")}
                                </label>
                                {/* <div className="d-flex align-items-center">
                                  <input
                                    type="radio"
                                    id="oneTime"
                                    name="type"
                                    value={1}
                                    checked={values.type === 1}
                                    onChange={() => setFieldValue("type", 1)}
                                  />
                                  <label htmlFor="oneTime" className="ms-2">
                                    {t("One-Time")}
                                  </label>
                                </div> */}

                                <label class="custom-radio">
                                  <input
                                    type="radio"
                                    id="recurring"
                                    name="type"
                                    value={2}
                                    checked={values.type === 2}
                                    onChange={() => setFieldValue("type", 2)}
                                  />
                                  <span class="checkmark"></span>
                                  {t("Recurring")}
                                </label>
                                {/* <div className="d-flex align-items-center">
                                  <input
                                    type="radio"
                                    id="recurring"
                                    name="type"
                                    value={2}
                                    checked={values.type === 2}
                                    onChange={() => setFieldValue("type", 2)}
                                  />
                                  <label htmlFor="recurring" className="ms-2">
                                    {t("Recurring")}
                                  </label>
                                </div> */}
                              </div>
                            </div>
                          </Col>

                          {values.type === 1 && (
                            <>
                              <Col lg={6}>
                                <div style={{ maxWidth: 500 }}>
                                  <Calendar
                                    id="calendar-12h"
                                    className="w-100"
                                    value={datetime12h}
                                    onChange={(e) => setDateTime12h(e.value)}
                                    hourFormat="12"
                                    inline
                                    minDate={new Date()}
                                  />
                                </div>
                                <div className="d-flex align-items-start gap-4 ">
                                  <TimePicker
                                    startTime={startTime}
                                    setStartTime={setStartTime}
                                    endTime={endTime}
                                    setEndTime={setEndTime}
                                  />
                                  <button
                                    type="button"
                                    onClick={() => {
                                      handleTimeSelect(
                                        values,
                                        setFieldValue,
                                        datetime12h
                                      );
                                    }}
                                    className="main_btn mt-3"
                                  >
                                    {t("Add Date")}
                                  </button>
                                </div>
                              </Col>
                              <Col lg={6}>
                                <div className="form_field mb-0">
                                  <CustomLabel
                                    htmlFor="dateAndTimes.startDate"
                                    label={t("Start On")}
                                  />
                                </div>
                                <div className="selected_date_time g_between mt-0">
                                  <div className="d-flex gap-2">
                                   
                                    <CalendarPrimaryIcon
                                      width={18}
                                      height={18}
                                      color={primaryColor}
                                    />
                                    <span>
                                      {formatDate(
                                        values?.dateAndTimes?.startDate
                                      )}
                                    </span>

                                    <span>{`${formatTime(
                                      values?.dateAndTimes?.startTime
                                    )} `}</span>
                                  </div>
                                  {values?.dateAndTimes?.startDate && (
                                    <button
                                      type="button"
                                      className="transparent_btn"
                                      onClick={() => {
                                        setFieldValue(
                                          "dateAndTimes.startDate",
                                          ""
                                        );
                                      }}
                                    >
                                      <CircleX className="text_danger" />
                                    </button>
                                  )}
                                </div>
                                <ErrorMessage
                                  name={"dateAndTimes.startDate"}
                                  component="div"
                                  className="text-danger"
                                />

                                <div className="form_field mb-0 mt-3">
                                  <CustomLabel
                                    htmlFor="dateAndTimes.endDate"
                                    label={t("Expires At")}
                                  />
                                </div>
                                <div className="selected_date_time g_between mt-0">
                                  <div className="d-flex gap-2">
                                    
                                    <CalendarPrimaryIcon
                                      width={18}
                                      height={18}
                                      color={primaryColor}
                                    />
                                    <span>
                                      {formatDate(
                                        values?.dateAndTimes?.endDate
                                      )}
                                    </span>
                                    <span>{`${formatTime(
                                      values?.dateAndTimes?.endTime
                                    )} `}</span>
                                  </div>
                                  {values?.dateAndTimes?.endDate && (
                                    <button
                                      type="button"
                                      className="transparent_btn"
                                      onClick={() => {
                                        setFieldValue(
                                          "dateAndTimes.endDate",
                                          ""
                                        );
                                      }}
                                    >
                                      <CircleX className="text_danger" />
                                    </button>
                                  )}
                                </div>
                                <ErrorMessage
                                  name={"dateAndTimes.endDate"}
                                  component="div"
                                  className="text-danger"
                                />
                              </Col>
                            </>
                          )}

                          {values.type === 2 && (
                            <>
                              <Col lg={6}>
                                <div className="form_field mb-0">
                                  <CustomLabel
                                    htmlFor="dateAndTimes.startTime"
                                    label={t("Closure Time")}
                                  />
                                </div>
                                <TimePicker
                                  startTime={startTime}
                                  setStartTime={setStartTime}
                                  endTime={endTime}
                                  setEndTime={setEndTime}
                                />
                              </Col>
                              <Col lg={6}>
                                <div className="form_field">
                                  <CustomLabel
                                    htmlFor="dateAndTimes.daysOfRecurrence"
                                    label={t("Days of Recurrence")}
                                  />
                                  <MultipleSelect
                                    value={
                                      values?.dateAndTimes?.daysOfRecurrence
                                    }
                                    handleMultipleSelectChange={(e) =>
                                      setFieldValue(
                                        "dateAndTimes.daysOfRecurrence",
                                        e
                                      )
                                    }
                                    options={weekDays}
                                    optionLabel="daysOfRecurrence"
                                    placeholder={"Days of Recurrence"}
                                    maxSelectedLabels={3}
                                    className={`${
                                      touched.dateAndTimes?.daysOfRecurrence &&
                                      errors.dateAndTimes?.daysOfRecurrence
                                        ? "invalid"
                                        : ""
                                    }`}
                                  />
                                  <ErrorMessage
                                    name={"dateAndTimes.daysOfRecurrence"}
                                    component="div"
                                    className="text-danger"
                                  />
                                </div>
                              </Col>
                            </>
                          )}

                          <div className="mt-3"></div>

                          <Col lg={6}>
                            <div className="form_field">
                              <CustomLabel
                                htmlFor="offerDetails.description"
                                label={t("English Closure Description")}
                              />
                              <CustomField
                                name="description"
                                placeholder="Enter English Closure Description"
                                touched={touched}
                                errors={errors}
                                as="textarea"
                              />
                            </div>
                          </Col>
                          <Col lg={6}>
                            <div className="form_field">
                              <CustomLabel
                                htmlFor="descriptionAr"
                                label={t("Arabic Closure Description")}
                              />
                              <CustomField
                                name="descriptionAr"
                                placeholder="Enter Arabic Closure Description"
                                touched={touched}
                                errors={errors}
                                as="textarea"
                                isArabic={true}
                              />
                            </div>
                          </Col>
                        </Row>
                      </div>
                      <div className="mt-5">
                        <FormButtons
                          isLoading={isLoading}
                          addBtnText={
                            mode === "add" ? t("Add New") : t("Update")
                          }
                        />
                      </div>
                    </div>
                  </Form>
                )}
              </Formik>
            </Row>
          </Container>
        )}
      </div>
    </React.Fragment>
  );
};

export default AddClosure;
